import { FC } from 'react'
import { ArrowRight } from 'lucide-react'
import CardInfoButton from './CardInfoButton'
import type { CardMeta } from './types'

const signMeta: CardMeta = {
  description: 'Recent crew sign-on and sign-off records across all vessels.',
  endpoint: 'GET /sign/on-off-records/all',
  logic: 'Displays the last 10 sign-on/off records sorted by date descending.',
}

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const s = (status || '').toUpperCase()
  const isOn = s === 'SIGNED_ON'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
      isOn ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isOn ? 'bg-emerald-500' : 'bg-red-500'}`} />
      {isOn ? 'Onboard' : 'Signed Off'}
    </span>
  )
}

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

interface Props {
  records: any[]
  onViewAll: () => void
}

const SignOnOffTable: FC<Props> = ({ records, onViewAll }) => {
  return (
    <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2'>
          <h6 className='text-sm font-bold text-slate-900'>Recent Sign-On / Off</h6>
          <CardInfoButton meta={signMeta} />
        </div>
        <button onClick={onViewAll} className='text-[10px] font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors'>
          View all <ArrowRight className='w-3 h-3' />
        </button>
      </div>
      <div className='overflow-x-auto max-h-[280px] overflow-y-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider'>
              <th className='text-left font-semibold py-2 pr-2'>Crew Name</th>
              <th className='text-left font-semibold py-2 px-2'>Vessel</th>
              <th className='text-left font-semibold py-2 px-2'>Date</th>
              <th className='text-left font-semibold py-2 px-2'>Port</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {records.length === 0 ? (
              <tr>
                <td colSpan={5} className='text-center text-slate-400 py-8 text-xs'>No sign-on/off records found</td>
              </tr>
            ) : (
              records.map((rec, i) => (
                <tr key={i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{rec.crewName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{rec.vesselName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-700'>{fmtDate(rec.signOnDate)}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{rec.portSignOn || '—'}</td>
                  <td className='py-2.5 pl-2 text-right'><StatusBadge status={rec.status} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default SignOnOffTable
